import { Guide } from '@/store/guide/types';
import { MeditationType, Program } from '@/store/program/types';

const COMPLETION_TYPES = new Set<string>([
	MeditationType.Sequential,
	MeditationType.Series,
	MeditationType.MasterClass,
	MeditationType.Spark,
	MeditationType.Movement,
	MeditationType.MeditationWithMusic,
	MeditationType.Hidden,
]);

const NO_DURATION_TYPES = new Set<string>([MeditationType.Timer, MeditationType.Freeform]);

// Sequential programs number their steps only once there are more than a couple of them.
const MIN_GUIDES_FOR_NUMBERING = 3;

export const isGuideLocked = (guide: Guide, isSubscribed: boolean): boolean =>
	!(guide.is_free || isSubscribed);

export const sortGuides = (guides: Guide[] = []): Guide[] =>
	[...guides].sort((a, b) => a.position - b.position);

// Web only knows the server's per-program progress, so a guide counts as completed when its
// position is at or below it.
export const isGuideCompleted = (guide: Guide, program: Program, progress: number): boolean =>
	COMPLETION_TYPES.has(program?.meditation_type) && guide.position <= progress;

export const isNextUpGuide = (guide: Guide, program: Program, progress: number): boolean => {
	if (
		program?.meditation_type !== MeditationType.Sequential &&
		program?.meditation_type !== MeditationType.MasterClass
	) {
		return false;
	}
	const next = sortGuides(program.guides).find(g => !g.is_trailer && !isGuideCompleted(g, program, progress));
	return next?.id === guide.id;
};

// A sequential row plays when it is first, follows a trailer, follows a completed row, or is
// itself completed. Every other program type plays freely.
export const isGuidePlayable = (guide: Guide, program: Program, progress: number): boolean => {
	if (program?.meditation_type !== MeditationType.Sequential) {
		return true;
	}
	const sorted = sortGuides(program.guides);
	const index = sorted.findIndex(g => g.id === guide.id);
	if (index <= 0) {
		return true;
	}
	const previous = sorted[index - 1];
	return (
		previous.is_trailer ||
		isGuideCompleted(previous, program, progress) ||
		isGuideCompleted(guide, program, progress)
	);
};

export const shouldShowDuration = (program: Program): boolean =>
	!NO_DURATION_TYPES.has(program?.meditation_type);

export const getGuideTitle = (guide: Guide, program: Program): string => {
	if (program?.meditation_type === MeditationType.MasterClass) {
		return `${guide.position - 1}. ${guide.title}`;
	}
	if (
		program?.meditation_type === MeditationType.Sequential &&
		(program.guides?.length ?? 0) >= MIN_GUIDES_FOR_NUMBERING
	) {
		return `${guide.position}. ${guide.title}`;
	}
	return guide.title;
};

export const getGuideSubtitle = (guide: Guide, program: Program): string | undefined => {
	if (program?.meditation_type === MeditationType.Music) {
		return guide.narrator_name?.trim() || undefined;
	}
	if (program?.meditation_type === MeditationType.MasterClass) {
		// Some subtitles arrive with a leading space, which shows as a visible indent.
		return guide.subtitle?.trim() || undefined;
	}
	return undefined;
};

// Sequential and masterclass programs resume at the next unfinished session once any progress
// exists, so the primary action reads "Play Next". Every other case starts the first session.
export const getPlayTarget = (program: Program, progress: number): { guide?: Guide; isResuming: boolean } => {
	const playable = sortGuides(program?.guides).filter(g => !g.is_trailer);
	const isProgressive =
		program?.meditation_type === MeditationType.Sequential ||
		program?.meditation_type === MeditationType.MasterClass;
	if (isProgressive && progress > 0) {
		const next = playable.find(g => !isGuideCompleted(g, program, progress));
		if (next) {
			return { guide: next, isResuming: true };
		}
	}
	return { guide: playable[0], isResuming: false };
};
